import { browserSessionStorage, browserLocalStorage } from "./browser-storage.ts";
const tokenKey = "edubgt_session_token";
const sessionExpiresAtKey = "edubgt_session_expires_at";
const sessionLastActivityKey = "edubgt_session_last_activity";
const sessionIdleTimeoutKey = "edubgt_session_idle_timeout";
const sessionExpiredNoticeKey = "edubgt_session_expired_notice";
const recentSessionKey = "edubgt_recent_session";
const studentCareReportsPendingKey = "edubgt_student_care_reports_pending";
const studentCareReportsVerifiedKey = "edubgt_student_care_reports_verified";

export const sessionExpiredEvent = "edubgt:session-expired";
export const sessionClearedEvent = "edubgt:session-cleared";
let sessionGeneration=0;
export function readSessionGeneration(){return sessionGeneration;}
export const sessionIdleTimeoutMs = 30 * 60 * 1000;
export const sessionWarningBeforeMs = 5 * 60 * 1000;

export type SessionInfo = {
  expiresAt: string;
  lastActivityAt: string;
  idleTimeoutSeconds: number;
};

export function saveToken(_token: string | null | undefined, session?: SessionInfo) {
  browserLocalStorage.removeItem(tokenKey);
  browserSessionStorage.removeItem(tokenKey);
  browserSessionStorage.removeItem(sessionExpiredNoticeKey);
  if (session) saveSessionInfo(session);
  else recordSessionActivity();
}

export function clearToken() {
  sessionGeneration++;
  if (typeof window !== "undefined") window.dispatchEvent(new Event(sessionClearedEvent));
  browserLocalStorage.removeItem(tokenKey);
  browserSessionStorage.removeItem(tokenKey);
  browserSessionStorage.removeItem(sessionExpiresAtKey);
  browserSessionStorage.removeItem(sessionLastActivityKey);
  browserSessionStorage.removeItem(sessionIdleTimeoutKey);
  browserSessionStorage.removeItem(studentCareReportsPendingKey);
  browserSessionStorage.removeItem(studentCareReportsVerifiedKey);
  browserLocalStorage.removeItem(recentSessionKey);
}

export function saveSessionInfo(session: SessionInfo) {
  const expiresAt = Date.parse(session.expiresAt);
  const lastActivityAt = Date.parse(session.lastActivityAt);
  if (Number.isFinite(expiresAt)) browserSessionStorage.setItem(sessionExpiresAtKey, String(expiresAt));
  browserLocalStorage.setItem(recentSessionKey, "1");
  if (Number.isFinite(lastActivityAt)) browserSessionStorage.setItem(sessionLastActivityKey, String(lastActivityAt));
  if (Number.isFinite(session.idleTimeoutSeconds) && session.idleTimeoutSeconds > 0) {
    browserSessionStorage.setItem(sessionIdleTimeoutKey, String(session.idleTimeoutSeconds * 1000));
  }
}

export function recordSessionActivity(at = Date.now()) {
  browserSessionStorage.setItem(sessionLastActivityKey, String(at));
}

export function readSessionExpiresAt() {
  const value = Number(browserSessionStorage.getItem(sessionExpiresAtKey));
  return Number.isFinite(value) && value > 0 ? value : null;
}

// A hint only (no session data): this browser had a session, so the dashboard is probably next.
export function hasRecentSession() {
  return readSessionExpiresAt() !== null || browserLocalStorage.getItem(recentSessionKey) === "1";
}

export function readSessionLastActivity() {
  const value = Number(browserSessionStorage.getItem(sessionLastActivityKey));
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function readSessionIdleTimeout() {
  const value = Number(browserSessionStorage.getItem(sessionIdleTimeoutKey));
  return Number.isFinite(value) && value > 0 ? value : sessionIdleTimeoutMs;
}

export function markSessionExpired() {
  browserSessionStorage.setItem(sessionExpiredNoticeKey, "1");
}

export function consumeSessionExpiredNotice() {
  const expired = browserSessionStorage.getItem(sessionExpiredNoticeKey) === "1";
  browserSessionStorage.removeItem(sessionExpiredNoticeKey);
  return expired;
}

export function requestStudentCareReportsUnlock(userId: string) {
  browserSessionStorage.setItem(studentCareReportsPendingKey, userId);
}

export function completeStudentCareReportsUnlock(userId: string, options: { allowWithoutPending?: boolean } = {}) {
  if (!options.allowWithoutPending && browserSessionStorage.getItem(studentCareReportsPendingKey) !== userId) return;
  browserSessionStorage.setItem(studentCareReportsVerifiedKey, userId);
  browserSessionStorage.removeItem(studentCareReportsPendingKey);
}

export function hasStudentCareReportsUnlock(userId: string) {
  return browserSessionStorage.getItem(studentCareReportsVerifiedKey) === userId;
}
